import React from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import { Shortlist, ShortlistCategoriesConfigProps } from '@modules/shortlist/types'
import AddIcon from '@mui/icons-material/Add'

import { ManageColumnsText, ManageColumnsWrapper, ShortlistButtonsBox } from '../../index.styles'
import { FilterShortlistsButton } from '../FilterShortlistsButton'
import { SearchField } from '../SearchField'
import CategoriesPopover from '../ShortlistTable/CategoriesPopover'
import { StyledColumnsConfigButton } from '../ShortlistTable/index.styles'

export interface ShortlistControlsProps {
  activeShortlist?: Shortlist
  searchQuery: string
  onSearchChange: (value: string) => void
  filtersCount: number
  onFilterClick: (event: React.MouseEvent<HTMLElement> | null) => void
  canFilter: boolean
  canCreateColumns: boolean
  onAddColumnClick: (event: React.MouseEvent<HTMLElement> | null) => void
  optionalColumnsConfig?: boolean
  updateTableConfig?: (config: ShortlistCategoriesConfigProps) => void
  anchorEl: Nullable<HTMLElement>
  setAnchorEl: (value: Nullable<HTMLElement>) => void
}

const ShortlistControls: React.FC<ShortlistControlsProps> = ({
  activeShortlist,
  searchQuery,
  anchorEl,
  onSearchChange,
  filtersCount,
  onFilterClick,
  canFilter,
  canCreateColumns,
  onAddColumnClick,
  optionalColumnsConfig,
  updateTableConfig,
  setAnchorEl,
}) => {
  const { t } = useTranslation()

  return (
    <ShortlistButtonsBox>
      <SearchField searchQuery={searchQuery} onSearchChange={onSearchChange} disabled={filtersCount > 0} />

      {canFilter && (
        <FilterShortlistsButton filtersCount={filtersCount} onClick={onFilterClick} isDisabled={!!searchQuery.length} />
      )}

      {canCreateColumns && (
        <ManageColumnsWrapper $width="180px" onClick={onAddColumnClick} id="shortlist-create_custom_column">
          <ManageColumnsText>{t(translationKeys.ADD_CUSTOM_COLUMN)}</ManageColumnsText>
          <StyledColumnsConfigButton data-testid="add-custom-column-button">
            <AddIcon fontSize="small" />
          </StyledColumnsConfigButton>
        </ManageColumnsWrapper>
      )}

      {optionalColumnsConfig && updateTableConfig && (
        <CategoriesPopover
          updateTableConfig={updateTableConfig}
          ndaFieldInitialValue={activeShortlist?.columns?.nda || { visible: false, width: 0 }}
          noteFieldInitialValue={activeShortlist?.columns?.note || { visible: false, width: 0 }}
          tagIdFieldInitialValue={activeShortlist?.columns?.tagId || { visible: false, width: 0 }}
          priorityInitialValue={activeShortlist?.columns?.priority || { visible: false, width: 0 }}
          dateAddedFieldInitialValue={activeShortlist?.columns?.createdAt || { visible: false, width: 0 }}
          companyFieldInitialValue={activeShortlist?.columns?.company || { visible: false, width: 0 }}
          lastEventFieldInitialValue={activeShortlist?.columns?.lastEvent || { visible: false, width: 0 }}
          futureEventsFieldInitialValue={activeShortlist?.columns?.futureEvents || { visible: false, width: 0 }}
          contactStatusFieldInitialValue={activeShortlist?.columns?.contactStatus || { visible: false, width: 0 }}
          updatedFieldInitialValue={activeShortlist?.columns?.updatedAt || { visible: false, width: 0 }}
          phaseFieldInitialValue={activeShortlist?.columns?.phase || { visible: false, width: 0 }}
          optionalColumnsConfig={activeShortlist?.columns}
          activeShortlist={activeShortlist}
          onUpsertCustomColumnClick={onAddColumnClick}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
        />
      )}
    </ShortlistButtonsBox>
  )
}

export default ShortlistControls
